import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { compose, filter, includes, isNil, map, sort } from 'ramda';
import { isNilOrEmpty, isObject } from 'ramda-extension';
import { useDispatch } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import useSetFormValue from '@gef-ui/form-core/hooks/state/useSetFormValue';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider } from 'components';
import AutocompleteCustom from 'components/atoms/GefMui/AutocompleteCustom';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import Subheader from 'components/atoms/GefMui/Subheader';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { startupDetailInfo } from 'features/views/taskCreatingPages/actions';
import {
	taskTypeBackoffice,
	taskTypeContactClient,
	taskTypeMobileScan,
	taskTypeSellerRequest,
} from 'features/views/taskCreatingPages/constants';
import {
	BACKOFFICE_FORM_NAME,
	CONTACT_CLIENT_FORM_NAME,
	FIELDS,
	MANUAL_LEAD_FORM_NAME,
	MOBILE_SCAN_FORM_NAME,
	SELLER_REQUEST_FORM_NAME,
} from 'features/views/taskCreatingPages/form';
import {
	getCorrectAclPermissionByPageType,
	getCorrectTaskTypesByPageType,
} from 'features/views/taskCreatingPages/utils';
import { sortAlphabeticallyBy } from 'utils';
import { getTommorowAsDateObj } from 'utils/apiDateTime';

export const DetailInfo = ({ formName, taskTypesCodelist = [], taskSubtypesCodelist = [], subheaderTitle }) => {
	const dispatch = useDispatch();
	const formData = useFormData();
	const formIsInitiated = isObject(formData);
	const setFormValue = useSetFormValue(formName);

	useEffect(() => {
		if (formIsInitiated && !isNilOrEmpty(taskTypesCodelist)) {
			dispatch(startupDetailInfo(formName));
		}
	}, [dispatch, formName, formIsInitiated, taskTypesCodelist]);

	const filterSubTypesByTask = useCallback(
		(formName) => (value) => {
			let taskSubTypeId;
			switch (formName) {
				case SELLER_REQUEST_FORM_NAME:
					taskSubTypeId = taskTypeSellerRequest;
					break;
				case BACKOFFICE_FORM_NAME:
					taskSubTypeId = taskTypeBackoffice;
					break;
				case MOBILE_SCAN_FORM_NAME:
					taskSubTypeId = taskTypeMobileScan;
					break;
				case CONTACT_CLIENT_FORM_NAME:
					taskSubTypeId = taskTypeContactClient;
					break;
				case MANUAL_LEAD_FORM_NAME:
					taskSubTypeId = !isNilOrEmpty(formData?.taskType) && formData?.taskType.toString();
					break;
			}
			return includes(taskSubTypeId, value.taskTypeIds);
		},
		[formData?.taskType],
	);

	const splitTaskTypeIds = ({ taskTypeIds, ...rest }) => ({
		...rest,
		taskTypeIds: taskTypeIds.split(','),
	});

	const generateTaskSubtypesOptions = useCallback(() => {
		return compose(
			sort(sortAlphabeticallyBy('description')),
			filter(filterSubTypesByTask(formName)),
			map(splitTaskTypeIds),
			filter(({ taskTypeIds }) => !isNil(taskTypeIds)),
		)(taskSubtypesCodelist);
	}, [taskSubtypesCodelist, filterSubTypesByTask, formName]);

	const generateTaskTypeOptions = useCallback(() => {
		if (formName === MANUAL_LEAD_FORM_NAME) {
			return compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => item.informative === 2 && item.manual === 1),
			)(taskTypesCodelist);
		} else {
			const taskTypes = getCorrectTaskTypesByPageType(formName);
			const aclPermission = getCorrectAclPermissionByPageType(formName);

			return compose(
				sort(sortAlphabeticallyBy('description')),
				filter(() => hasPermissionByIds(aclPermission)),
				filter((item) => includes(+item.id, taskTypes)),
			)(taskTypesCodelist);
		}
	}, [formName, taskTypesCodelist]);

	const generateSubTypeLabel = useCallback(() => {
		switch (formName) {
			case SELLER_REQUEST_FORM_NAME:
				return getLabelMessage('taskCreatingPages:taskSubTypeSellerRequest');
			case MANUAL_LEAD_FORM_NAME:
				return getLabelMessage('taskCreatingPages:leadSubType');
			default:
				return getLabelMessage('taskCreatingPages:taskSubType');
		}
	}, [formName]);

	const resetTaskSubType = useCallback(() => {
		setFormValue({ fieldName: FIELDS.taskSubTypeId, value: null });
	}, [setFormValue]);

	const sectionHeaderText =
		formName === MANUAL_LEAD_FORM_NAME
			? getLabelMessage('taskCreatingPages:leadDetail')
			: getLabelMessage('taskCreatingPages:taskDetail');

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<Stack spacing={2}>
				<Subheader title={subheaderTitle} />
				<SectionHeader text={sectionHeaderText} />
				<Grid container>
					<Grid size={12}>
						<Grid container spacing={2}>
							<Grid size={4}>
								<AutocompleteCustom
									size="small"
									fieldName={FIELDS.taskType}
									label={
										formName === MANUAL_LEAD_FORM_NAME
											? getLabelMessage('taskCreatingPages:leadType')
											: getLabelMessage('taskCreatingPages:taskType')
									}
									options={generateTaskTypeOptions()}
									labelPropName="description"
									onChange={resetTaskSubType}
									disabled={formName !== MANUAL_LEAD_FORM_NAME}
								/>
							</Grid>
							<Grid size={4}>
								<AutocompleteCustom
									size="small"
									fieldName={FIELDS.taskSubTypeId}
									label={generateSubTypeLabel()}
									options={generateTaskSubtypesOptions()}
									labelPropName="description"
									required
									disabled={isNilOrEmpty(formData?.taskType)}
								/>
							</Grid>

							{includes(formName, [
								SELLER_REQUEST_FORM_NAME,
								BACKOFFICE_FORM_NAME,
								MOBILE_SCAN_FORM_NAME,
							]) && (
								<>
									<Grid size={2}>
										<DatePickerFullWidth
											label={getLabelMessage('taskCreatingPages:fulfillDate')}
											fieldName={FIELDS.fulfillDate}
											minDate={getTommorowAsDateObj()}
											required
											validationDisplay={
												!formData?.fulfillDate ? validationDisplayTypes.HIDDEN : undefined
											}
										/>
									</Grid>
									<Grid size={2}>
										<DatePickerFullWidth
											label={getLabelMessage('taskCreatingPages:warningDate')}
											fieldName={FIELDS.warningDate}
											minDate={getTommorowAsDateObj()}
											required
											validationDisplay={
												!formData?.warningDate ? validationDisplayTypes.HIDDEN : undefined
											}
										/>
									</Grid>
								</>
							)}

							{formName === SELLER_REQUEST_FORM_NAME && (
								<Grid size={12}>
									<TextField
										size="small"
										fieldName={FIELDS.additionalContactReason}
										label={getLabelMessage('taskCreatingPages:additionalContactReason')}
										slotProps={{
											htmlInput: {
												maxLength: MaxLengths.ADDITIONAL_INFO,
											},
										}}
									/>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
};

DetailInfo.propTypes = {
	formName: PropTypes.string,
	subheaderTitle: PropTypes.string,
	taskSubtypesCodelist: PropTypes.array,
	taskTypesCodelist: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.taskSubtypes, 'taskSubtypesCodelist'),
	codelistProvider(codelists.taskTypes, 'taskTypesCodelist'),
)(DetailInfo);
